import * as React from "react";
import { styled } from "styled-components";
import { Link } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { cssSpacing } from "../../util/styles.js";
import { Dots, useCarousel } from "../../hooks/useCarousel.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { useLinkProps } from "../../hooks/useLinkProps.js";
import { chevronLeft, chevronRight } from "../../icons/index.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";

export interface LogoSliderProps {
  title?: string;
  list?: LogoItem[];
}

interface LogoItem {
  id: string;
  image?: Image;
  link?: Link;
}

function useResponsiveLimit() {
  const getLimit = () => {
    if (typeof window === "undefined") return 4;
    const w = window.innerWidth;
    if (w < 480) return 1;
    if (w < 768) return 2;
    if (w < 960) return 3;
    return 4;
  };

  const [limit, setLimit] = React.useState(getLimit);

  React.useEffect(() => {
    const onResize = () => setLimit(getLimit());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return limit;
}

export const LogoSlider: React.FC<LogoSliderProps> = ({
  title = "",
  list = [],
}) => {
  const limit = useResponsiveLimit();
  const carouseItems = React.useMemo(() => list.filter((x) => x.image), [list]);
  const { items, active, dots, setSlice, prev, next } = useCarousel(
    carouseItems,
    {
      limit,
      fillLast: true,
    }
  );

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          <LogoSliderH2>{title}</LogoSliderH2>
          <LogoSliderList $columns={limit}>
            <ArrowButton onClick={prev}>{chevronLeft}</ArrowButton>
            {items.map((item) => (
              <Item key={item.id} {...item} />
            ))}
            <ArrowButton onClick={next}>{chevronRight}</ArrowButton>
          </LogoSliderList>
          <Dots dots={dots} active={active} setSlice={setSlice} />
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

const Item: React.FC<LogoItem> = ({ image, link }) => {
  const linkProps = useLinkProps(link);
  const imageProps = useImageProps(image, {
    width: 240,
    height: 120,
    fit: "contain",
  });

  if (linkProps) {
    return (
      <a {...linkProps}>
        <img {...imageProps} />
      </a>
    );
  }

  return <img {...imageProps} />;
};

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    width: 21px;
    height: 21px;
  }
`;

const LogoSliderH2 = styled.h2`
  text-align: center;
`;

const LogoSliderList = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: 30px repeat(${(p) => p.$columns}, 1fr) 30px;
  gap: ${cssSpacing("m")};
  align-items: center;
  justify-content: center;
  margin: ${cssSpacing("m", 0, "m", 0)};

  > * {
    justify-self: center;
    display: inherit;
  }

  img {
    height: 60px;
    width: 120px;
  }
`;
